import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
    Loader2, 
    Check, 
    X, 
    Mail, 
    User, 
    KeyRound, 
    AlertTriangle,
    ShieldAlert, 
    Trash2,
    AlertCircle,
    CheckCircle2,
    Ban,
    XCircle
} from 'lucide-react';
import { updateProfile, deleteProfile, clearAuthTokens } from '@/api';
import { useAuth } from "@/context/AuthContext"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, } from "@/components/ui/dialog";
import { motion, AnimatePresence } from 'framer-motion';

const passwordRequirements = [
    { id: 'length', label: 'At least 8 characters', regex: /.{8,}/ },
    { id: 'capital', label: 'At least one capital letter', regex: /[A-Z]/ },
    { id: 'lowercase', label: 'At least one lowercase letter', regex: /[a-z]/ },
    { id: 'number', label: 'At least one number', regex: /[0-9]/ },
    { id: 'special', label: 'At least one special character', regex: /[!@#$%^&*(),.?":{}|<>]/ },
];

export function AccountSettings() {
    const { error, success } = useEnhancedToast();
    const navigate = useNavigate();
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
    const [meetsRequirements, setMeetsRequirements] = useState({
        length: false,
        capital: false,
        lowercase: false,
        number: false,
        special: false,
    });

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const [deleteConfirmPhrase, setDeleteConfirmPhrase] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const { user, refreshUser } = useAuth();

    const [nameData, setNameData] = useState({
        firstName: user?.first_name,
        lastName: user?.last_name,
    });

    // Check password requirements
    useEffect(() => {
        const { newPassword } = passwordData;

        if (newPassword.length > 0 && !showPasswordRequirements) {
            setShowPasswordRequirements(true);
        } else if (newPassword.length === 0 && showPasswordRequirements) {
            setShowPasswordRequirements(false);
        }

        const updatedRequirements = passwordRequirements.reduce((acc, req) => ({
            ...acc,
            [req.id]: req.regex.test(newPassword)
        }), {});

        setMeetsRequirements(updatedRequirements);
    }, [passwordData.newPassword]);

    const handlePasswordChange = async () => {
        const allRequirementsMet = Object.values(meetsRequirements).every(Boolean);
        const passwordsMatch = passwordData.newPassword === passwordData.confirmPassword;

        if (!passwordsMatch) {
            errorRef.current({
                icon: XCircle,
                title: "Passwords Don't Match",
                description: "Please ensure both passwords are identical",
                duration: 4000,
            });
            return;
        }

        if (!allRequirementsMet) {
            errorRef.current({
                icon: ShieldAlert,
                title: "Invalid Password",
                description: "Please meet all password requirements",
                duration: 4000,
            });
            return;
        }

        setIsPasswordLoading(true);
        try {
            await updateProfile({
                password: passwordData.newPassword
            });
            successRef.current({
                icon: CheckCircle2,
                title: "Password Updated",
                description: "Your password has been updated successfully",
                duration: 4000,
            });
            setPasswordData({ newPassword: '', confirmPassword: '' });
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Update Failed",
                description: "Could not update password. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsPasswordLoading(false);
        }
    };

    const handleNameUpdate = async () => {
        if (!nameData.firstName || !nameData.lastName) {
            errorRef.current({
                icon: AlertCircle,
                title: "Missing Information",
                description: "Both first name and last name are required",
                duration: 4000,
            });
            return;
        }

        setIsProfileLoading(true);
        try {
            await updateProfile({
                first_name: nameData.firstName,
                last_name: nameData.lastName
            });
            refreshUser();
            successRef.current({
                icon: CheckCircle2,
                title: "Profile Updated",
                description: "Your name has been updated successfully",
                duration: 4000,
            });
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Update Failed",
                description: "Could not update your profile. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsProfileLoading(false);
        }
    };

    const handleAccountDeletion = async () => {
        if (deleteConfirmPhrase.toLowerCase() !== 'delete my account') {
            errorRef.current({
                icon: Ban,
                title: "Invalid Confirmation",
                description: "Please type the confirmation phrase exactly as shown",
                duration: 4000,
            });
            return;
        }

        setIsDeleting(true);
        try {
            const response = await deleteProfile();
            if (!response.error) {
                successRef.current({
                    icon: CheckCircle2,
                    title: "Account Deleted",
                    description: "Your account has been deleted successfully",
                    duration: 4000,
                });
                setShowDeleteConfirmation(false);

                // First clear auth state and tokens
                clearAuthTokens();
                setIsDeleting(false);

                // Then navigate to login
                navigate('/auth/login', { replace: true });
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Delete Failed",
                description: err instanceof Error ? err.message : "Could not delete your account",
                duration: 4000,
            });
            setIsDeleting(false);
        }
    };

    return (
        <div className="grid gap-6 md:grid-cols-2">
            {/* Left Column - Profile Information */}
            <div className="space-y-6">
                {/* Basic Information Card */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <User className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Profile Information</CardTitle>
                                <CardDescription>Manage your personal information</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6 p-6">
                        {/* Email Display */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <Mail className="h-4 w-4 text-primary" />
                                <span>Email Address</span>
                            </Label>
                            <Input
                                type="email"
                                value={user?.email}
                                disabled
                                className="bg-muted"
                            />
                        </div>

                        {/* Name Fields */}
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="firstName" className="flex items-center gap-2 text-sm font-medium">
                                    <User className="h-4 w-4 text-primary" />
                                    First Name
                                </Label>
                                <Input
                                    id="firstName"
                                    type="text"
                                    value={nameData.firstName}
                                    onChange={(e) => setNameData(prev => ({ ...prev, firstName: e.target.value }))}
                                    placeholder="Enter your first name"
                                    className="h-9"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lastName" className="flex items-center gap-2 text-sm font-medium">
                                    <User className="h-4 w-4 text-primary" />
                                    Last Name
                                </Label>
                                <Input
                                    id="lastName"
                                    type="text"
                                    value={nameData.lastName}
                                    onChange={(e) => setNameData(prev => ({ ...prev, lastName: e.target.value }))}
                                    placeholder="Enter your last name"
                                    className="h-9"
                                />
                            </div>
                        </div>

                        {/* Action Button */}
                        <div className="flex justify-end">
                            <Button
                                onClick={handleNameUpdate}
                                disabled={isProfileLoading || (!nameData.firstName && !nameData.lastName)}
                                className="gap-2"
                                size="sm"
                            >
                                {isProfileLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <User className="h-4 w-4" />
                                        Update Profile
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Password Update Card */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <KeyRound className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Security</CardTitle>
                                <CardDescription>Update your password</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6 p-6">
                        <div className="space-y-2">
                            <Label htmlFor="newPassword" className="flex items-center gap-2 text-sm font-medium">
                                <KeyRound className="h-4 w-4 text-primary" />
                                New Password
                            </Label>
                            <Input
                                id="newPassword"
                                type="password"
                                value={passwordData.newPassword}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                                placeholder="Enter new password"
                                className="h-9"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-medium">
                                <KeyRound className="h-4 w-4 text-primary" />
                                Confirm Password
                            </Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={passwordData.confirmPassword}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                placeholder="Confirm new password"
                                className="h-9"
                            />
                        </div>

                        {/* Action Button */}
                        <div className="flex justify-end">
                            <Button
                                onClick={handlePasswordChange}
                                disabled={isPasswordLoading || !Object.values(meetsRequirements).every(Boolean) || passwordData.newPassword !== passwordData.confirmPassword || !passwordData.newPassword}
                                className="gap-2"
                                size="sm"
                            >
                                {isPasswordLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Updating Password...
                                    </>
                                ) : (
                                    <>
                                        <KeyRound className="h-4 w-4" />
                                        Update Password
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
                {/* Delete Account */}
                <Card className="shadow-sm border-destructive/30 hover:border-destructive/40 transition-colors">
                    <CardHeader className="border-b bg-destructive/5">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/10">
                                <ShieldAlert className="h-4 w-4 text-destructive" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle className="text-destructive">Account Deletion</CardTitle>
                                <CardDescription>Permanently delete your account and all associated data</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <Alert variant="warning" className="bg-amber-50/50 dark:bg-amber-950/20">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle className="text-sm">Important Notice</AlertTitle>
                            <AlertDescription className="text-xs">
                                For security and data integrity purposes, account deletion requires verification and must be processed 
                                through your organization's support team. Please reach out to your designated support contact to initiate 
                                the account deletion process. They will guide you through the necessary steps and ensure all your data 
                                is handled appropriately.
                            </AlertDescription>
                        </Alert>
                    </CardContent>
                </Card>

                {/* Password Requirements - Animated */}
                <AnimatePresence>
                    {showPasswordRequirements && (
                        <motion.div
                            initial={{ opacity: 0, y: 20, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: 20, height: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                            <Card className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                            <ShieldAlert className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex-1 space-y-1">
                                            <CardTitle>Password Requirements</CardTitle>
                                            <CardDescription>Meet these criteria for a strong password</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6">
                                    <div className="space-y-3">
                                        {passwordRequirements.map((req) => (
                                            <div key={req.id} className="flex items-center gap-2">
                                                {meetsRequirements[req.id] ? (
                                                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                                                ) : (
                                                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                                                )}
                                                <span className="text-sm">{req.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
